import React from 'react'
// Direct-module import, not the package barrel — see index.ts's re-export comment for why.
import { useAuthStore } from '../../../../passport/packages/@passport/store/src/authStore'
import { UserMenuButton } from './UserMenuButton'
import { SignInButton } from './SignInButton'
import { NotificationsButton } from './NotificationsButton'

/** A single "Developer Tools" row entry (the flow-studio Business Apps gallery, §2.4/§4.6 of the design doc). */
export interface DevToolLink {
  label: string
  icon?: React.ReactNode
  href?: string
  onClick?: () => void
}

/** A single "Apps Tools" row entry — shortcuts specific to the current app. */
export interface AppToolLink {
  label: string
  icon?: React.ReactNode
  href?: string
  onClick?: () => void
}

/** A single notification list entry rendered by `NotificationsButton`. */
export interface NotificationItem {
  id: string
  title: string
  body?: string
  timestamp: string
  read: boolean
  href?: string
  onClick?: () => void
}

export interface AuthHeaderControlsProps {
  /** Route or absolute URL for "View User Profile". Point this at wherever the
   *  consuming app renders `@passport/pages`' `ProfilePage` — this package does not
   *  ship a profile page of its own. Placeholder-only in app-template today (no real
   *  routing is wired up there yet); see AppShell.tsx for the exact placeholder used. */
  profileUrl?: string
  /** Route or absolute URL for "View App Settings". Point this at wherever the
   *  consuming app renders `@passport/pages`' `SettingsPage`. Same placeholder caveat
   *  as `profileUrl`. */
  appSettingsUrl?: string
  /** Entries for the "Developer Tools" row (the flow-studio Business Apps gallery).
   *  Omit the prop entirely (not an empty array) to hide the row. Independent of
   *  role — see `requiredDevToolsRole` for the actual visibility gate. */
  devTools?: DevToolLink[]
  /** Role ID (matched via `useAuthzStore().hasRole()`, which checks `Role.id` — NOT
   *  `Role.name`) the current user must have for the "Developer Tools" row to render
   *  at all, regardless of whether `devTools` was supplied. Defaults to 'IsDeveloper'. */
  requiredDevToolsRole?: string
  /** Entries for the "Apps Tools" row — shortcuts specific to the current app,
   *  distinct from the generic "View App Settings" row. Not role-gated. Omit to hide
   *  the row entirely. */
  appTools?: AppToolLink[]
  /** Notification list + count. Omit to hide the notifications button entirely — no
   *  frontend client for the notifications backend exists yet (design doc §2.8). */
  notifications?: {
    items: NotificationItem[]
    unreadCount: number
    onMarkAllRead?: () => void
    viewAllUrl?: string
  }
  /** Where "Sign In" links to when logged out. Required — each app's real /login
   *  route differs and `@passport/store` has no app-wide "where is login" config to
   *  default to. */
  signInUrl: string
  /** Called after logout succeeds — e.g. app-specific extra cleanup or a redirect. */
  onAfterLogout?: () => void
  /** Which cross-app handoff mechanism this app expects back on its next sign-in.
   *  Passed straight through to `UserMenuButton` — see its own doc comment for why
   *  this matters (logout silently drops back to the legacy `?token=` handoff on the
   *  next sign-in otherwise, for any app that migrated sign-in to `'code'`). */
  handoffMode?: 'token' | 'code'
}

/**
 * `AuthHeaderControls` — the composite meant for `AppHeader`'s `rightContent` slot.
 *
 * Renders left to right: `NotificationsButton`, `UserMenuButton` while logged in.
 * While logged out, renders `SignInButton` instead of both (design doc §4.1/§4.3).
 * While the persisted auth store is still rehydrating (`isLoading`), renders nothing,
 * to avoid a flash of "Sign In" immediately replaced by the user's avatar.
 */
export function AuthHeaderControls({
  profileUrl,
  appSettingsUrl,
  devTools,
  requiredDevToolsRole,
  appTools,
  notifications,
  signInUrl,
  onAfterLogout,
  handoffMode,
}: AuthHeaderControlsProps): React.ReactElement | null {
  const { isAuthenticated, isLoading } = useAuthStore()

  if (isLoading) {
    return null
  }

  if (!isAuthenticated) {
    return <SignInButton signInUrl={signInUrl} />
  }

  return (
    <div className="auth-header-controls">
      {notifications && (
        <NotificationsButton
          items={notifications.items}
          unreadCount={notifications.unreadCount}
          onMarkAllRead={notifications.onMarkAllRead}
          viewAllUrl={notifications.viewAllUrl}
        />
      )}
      <UserMenuButton
        profileUrl={profileUrl}
        appSettingsUrl={appSettingsUrl}
        devTools={devTools}
        requiredDevToolsRole={requiredDevToolsRole}
        appTools={appTools}
        onAfterLogout={onAfterLogout}
        handoffMode={handoffMode}
      />
      <style>{`
        .auth-header-controls {
          display: flex;
          align-items: center;
          gap: 8px;
        }
      `}</style>
    </div>
  )
}

export default AuthHeaderControls
